import {
  clearAdminToken,
  clearInvestorToken,
  getAdminToken,
  getInvestorToken,
} from '../utils/auth';

const API_BASE = process.env['REACT_APP_API_URL'] || 'http://localhost:8000/api/portal';

type ApiMessage = { message?: string };

export type CargoStory = { text?: string; mediaUrls?: string[] };

export type Cargo = {
  _id: string;
  productBeingShipped: string;
  quantity: number;
  purchaseLocation: string;
  purchasePrice: number;
  currency: string;
  shippingDestination: string;
  shippingPrice: number;
  otherExpenses: number;
  estimatedTimeOfArrival: string;
  estimatedTimeOfSelling: string;
  assignedInvestorIds: string[];
  shippingType?: 'sea' | 'air' | 'land';
  cargoDescription?: string;
  story?: CargoStory;
  hidden?: boolean;
  coverImageUrl?: string;
  purchaseDate?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type InvestorRecord = {
  _id: string;
  name: string;
  username: string;
  investmentAmount?: number;
  profitPercentageOnInvestment?: number;
  estimatedROI?: number;
  currency?: string;
  location?: string;
  assignedCargoIds: string[];
  assignedInvestmentIds: string[];
  createdAt?: string;
  updatedAt?: string;
};

export type PublicMapInvestor = {
  name: string;
  avatar: string;
  avatarImageUrl: string;
  location: string;
  investmentCount: number;
};

export type AvatarData = {
  _id: string;
  name: string;
  imageUrl: string;
  defaultTheme: number;
  secret: boolean;
};

export type PublicMapCargo = {
  _id: string;
  productBeingShipped: string;
  shippingType: 'sea' | 'air' | 'land';
  purchaseLocation: string;
  shippingDestination: string;
  estimatedTimeOfArrival: string;
  purchaseDate?: string;
  createdAt: string;
};

export type PublicMapStats = {
  totalInvested: number;
  totalExpectedProfit: number;
  activeInvestments: number;
  activeShipments: number;
  countryCount: number;
  investorCount: number;
  journeyCount: number;
  boutiqueCount: number;
};

export type PublicMapInvestment = {
  _id: string;
  title: string;
  status: InvestmentStatus;
  currency: string;
  minimumInvestment: number;
  cargoCount: number;
  investorCount: number;
  location: string;
};

export type PublicMapBoutique = {
  _id: string;
  name: string;
  logoUrl: string;
  description: string;
  location: string;
};

export type PublicMapJourney = {
  _id: string;
  title: string;
  tagline: string;
  location: string;
  locationLat: number;
  locationLng: number;
  spotsRemaining: number;
  status: 'active' | 'full' | 'past';
  coverImageUrl: string;
};

export type PublicMapData = {
  investors: PublicMapInvestor[];
  cargos: PublicMapCargo[];
  investments: PublicMapInvestment[];
  boutiques: PublicMapBoutique[];
  journeys: PublicMapJourney[];
  partners: PublicMapPartner[];
  stats: PublicMapStats;
};

export type DurationOption = {
  label: string;
  price: number;
  currency: string;
  description: string;
};

export type JourneyStatus = 'draft' | 'active' | 'full' | 'past';

export type Journey = {
  _id: string;
  title: string;
  tagline: string;
  story: string;
  location: string;
  locationLat: number;
  locationLng: number;
  coverImageUrl: string;
  coverVideoUrl: string;
  gallery: string[];
  durations: DurationOption[];
  included: string[];
  notIncluded: string[];
  maxGroupSize: number;
  spotsRemaining: number;
  guideName: string;
  guidePhoto: string;
  guideBio: string;
  guideQuote: string;
  availableDates: string[];
  status: JourneyStatus;
  createdAt?: string;
  updatedAt?: string;
};

export type JourneyInterestInput = {
  journeyId: string;
  journeyTitle: string;
  fullName: string;
  contactMethod: 'whatsapp' | 'email';
  contactDetail: string;
  preferredDuration: string;
  preferredDates: string;
  note: string;
  website?: string;
};

export type InvestmentStatus = 'active' | 'in_progress' | 'waiting' | 'successful';

export type Investment = {
  _id: string;
  title: string;
  description: string;
  currency: string;
  minimumInvestment: number;
  cargoIds: string[];
  assignedInvestorIds: string[];
  status?: InvestmentStatus;
  currentStatus?: string;
  hidden?: boolean;
  coverImageUrl?: string;
  location?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PublicInvestment = {
  _id: string;
  title: string;
  description: string;
  currency: string;
  minimumInvestment: number;
  status: InvestmentStatus;
  cargoCount: number;
  investorCount: number;
  coverImageUrl?: string;
  location?: string;
};

export type ProductVariant = {
  label: string;
  price: number;
};

export type PriceMatrixOption = {
  label: string;
  price: number;
  currency: string;
};

export type PriceMatrixRow = {
  label: string;
  options: PriceMatrixOption[];
};

export type ProductSection = 'food' | 'artisanal';

export type Product = {
  _id: string;
  name: string;
  description: string;
  origin: string;
  originStory: string;
  price: number;
  currency: string;
  variants: ProductVariant[];
  priceMatrix: PriceMatrixRow[];
  customOrderAvailable: boolean;
  customOrderNote: string;
  stock: number;
  coverImageUrl: string;
  images: string[];
  section: ProductSection;
  category: string;
  active: boolean;
  boutiqueId?: string;
  position?: number;
  internationalShipping: boolean;
  waitlistCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

/** Public-facing product (no `active` flag — only active products are returned). */
export type PublicProduct = Omit<Product, 'active' | 'createdAt' | 'updatedAt'>;

export type ProductInput = Omit<Product, '_id' | 'createdAt' | 'updatedAt'>;

export type BoutiqueSocialLinks = {
  instagram?: string;
  website?: string;
};

export type PublicBoutique = {
  _id: string;
  name: string;
  tagline: string;
  bio: string;
  location: string;
  coverImageUrl: string;
  profileImageUrl: string;
  logoUrl: string;
  category: string;
  section: 'earth' | 'hands';
  accentColor: string;
  shopThemeIndex?: number;
  socialLinks: BoutiqueSocialLinks;
  productCount: number;
};

export type Boutique = {
  _id: string;
  name: string;
  tagline: string;
  bio: string;
  originStory: string;
  location: string;
  locationLat?: number;
  locationLng?: number;
  coverImageUrl: string;
  profileImageUrl: string;
  logoUrl: string;
  galleryUrls: string[];
  category: string;
  section: 'earth' | 'hands';
  accentColor: string;
  shopThemeIndex?: number;
  active?: boolean;
  linkedJourneyIds: string[];
  socialLinks: BoutiqueSocialLinks;
  createdAt?: string;
  updatedAt?: string;
};

export type BoutiqueInput = Omit<Boutique, '_id' | 'createdAt' | 'updatedAt'>;

export type ProductOrderInput = {
  productId: string;
  variant?: string;
  quantity: number;
  fullName: string;
  contactMethod: 'whatsapp' | 'email';
  contactDetail: string;
  country: string;
  message?: string;
  /** Honeypot — left empty by real users; filled values are rejected server-side. */
  website?: string;
};

export type SocialLink = {
  platform: string;
  label: string;
  url: string;
};

export type Partner = {
  _id: string;
  name: string;
  logoUrl: string;
  title: string;
  description: string;
  active?: boolean;
  location?: string;
  locationLat?: number;
  locationLng?: number;
  createdAt?: string;
  updatedAt?: string;
};

export type PartnerInput = {
  name: string;
  logoUrl: string;
  title: string;
  description: string;
  active: boolean;
  location?: string;
  locationLat?: number;
  locationLng?: number;
};

export type PublicMapPartner = {
  _id: string;
  name: string;
  title: string;
  logoUrl: string;
  location: string;
  locationLat: number;
  locationLng: number;
};

export type SiteContent = {
  key: string;
  title?: string;
  body?: string;
  mediaUrls?: string[];
  links?: SocialLink[];
};

export type ContactRequest = {
  _id: string;
  type?: 'investment' | 'contact_us' | 'journey_interest';
  investmentId?: string;
  investmentTitle?: string;
  journeyId?: string;
  journeyTitle?: string;
  preferredDuration?: string;
  preferredDates?: string;
  fullName: string;
  contactMethod: 'whatsapp' | 'email';
  contactDetail: string;
  rdvDate?: string;
  note?: string;
  status: 'new' | 'read' | 'contacted';
  createdAt: string;
};

export type ProductOrder = {
  _id: string;
  productId: string;
  productName: string;
  variant: string;
  quantity: number;
  unitPrice: number;
  currency: string;
  total: number;
  fullName: string;
  contactMethod: 'whatsapp' | 'email';
  contactDetail: string;
  country: string;
  message?: string;
  boutiqueName?: string;
  status: 'new' | 'read' | 'contacted';
  createdAt: string;
};

export type AdminDashboardResponse = {
  cargos: Cargo[];
  investors: InvestorRecord[];
  investments: Investment[];
  unreadContactCount: number;
  unreadOrderCount: number;
};

export type InvestorProfile = {
  name: string;
  displayName: string;
  username: string;
  email?: string;
  investmentAmount?: number;
  profitPercentageOnInvestment?: number;
  estimatedROI?: number;
  currency: string;
  preferredCurrency: string;
  avatar?: string;
  preferredTheme?: number;
  kycCompleted: boolean;
  registrationMethod?: 'manual' | 'email' | 'google';
  hasPassword?: boolean;
  googleLinked?: boolean;
  newsletterOptIn?: boolean;
  assignedInvestmentIds?: string[];
};

export type InvestorInvestmentStatus = {
  _id: string;
  title: string;
  currentStatus: string;
};

export type InvestorHomeResponse = {
  investor: InvestorProfile;
  cargos: Cargo[];
  investments: InvestorInvestmentStatus[];
};

async function parseJsonOrThrow<T>(response: Response): Promise<T> {
  const payload = (await response.json()) as T & ApiMessage;

  if (!response.ok) {
    throw new Error(payload.message || 'Request failed');
  }

  return payload;
}

async function request<T>(path: string, init?: RequestInit, token?: string | null): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
    ...init,
  });

  return parseJsonOrThrow<T>(response);
}

export async function loginAdmin(username: string, password: string): Promise<{ token: string }> {
  return request<{ token: string }>('/admin/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export async function logoutAdmin(): Promise<void> {
  await request('/admin/logout', { method: 'POST' }, getAdminToken());
  clearAdminToken();
}

export async function getAdminDashboard(): Promise<AdminDashboardResponse> {
  return request<AdminDashboardResponse>('/admin/dashboard', { method: 'GET' }, getAdminToken());
}

export async function createCargo(payload: Omit<Cargo, '_id' | 'assignedInvestorIds' | 'createdAt' | 'updatedAt'> & { storyText?: string; storyMediaUrls?: string[] }): Promise<Cargo> {
  const response = await request<{ cargo: Cargo }>('/admin/cargos', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());

  return response.cargo;
}

export async function updateCargo(id: string, payload: Omit<Cargo, '_id' | 'assignedInvestorIds' | 'createdAt' | 'updatedAt'> & { storyText?: string; storyMediaUrls?: string[] }): Promise<Cargo> {
  const response = await request<{ cargo: Cargo }>(`/admin/cargos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());

  return response.cargo;
}

export async function deleteCargo(id: string): Promise<void> {
  await request<{ message: string }>(`/admin/cargos/${id}`, {
    method: 'DELETE',
  }, getAdminToken());
}

export async function getPublicMapData(): Promise<PublicMapData> {
  return request<PublicMapData>('/public/map-data', { method: 'GET' });
}

export async function uploadMedia(file: File): Promise<string> {
  const adminToken = getAdminToken();
  if (!adminToken) throw new Error('Not authenticated.');

  const form = new FormData();
  form.append('file', file);

  const base = (process.env['REACT_APP_API_URL'] || 'http://localhost:8000/api/portal').replace(/\/$/, '');
  const apiBase = base.replace(/\/api\/portal$/, '');

  const res = await fetch(`${apiBase}/api/portal/admin/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${adminToken}` },
    body: form,
  });

  const payload = (await res.json()) as { url?: string; message?: string };
  if (!res.ok) throw new Error(payload.message || 'Upload failed');
  return payload.url!;
}

export async function getPublicInvestments(): Promise<{ investments: PublicInvestment[] }> {
  return request<{ investments: PublicInvestment[] }>('/public/investments', { method: 'GET' });
}

export async function getPublicSiteContent(key: string): Promise<{ content: SiteContent }> {
  return request<{ content: SiteContent }>(`/public/site-content/${key}`, { method: 'GET' });
}

export async function updateSiteContent(key: string, payload: Omit<SiteContent, 'key'>): Promise<{ content: SiteContent }> {
  return request<{ content: SiteContent }>(`/admin/site-content/${key}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());
}

export async function createInvestor(payload: {
  name: string;
  username: string;
  password: string;
  investmentAmount: number;
  profitPercentageOnInvestment: number;
  currency: string;
  location?: string;
  investmentIds: string[];
}): Promise<InvestorRecord> {
  const response = await request<{ investor: InvestorRecord }>('/admin/investors', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());

  return response.investor;
}

export async function updateInvestor(id: string, payload: {
  name: string;
  username: string;
  password: string;
  investmentAmount: number;
  profitPercentageOnInvestment: number;
  currency: string;
  location?: string;
  investmentIds: string[];
}): Promise<InvestorRecord> {
  const response = await request<{ investor: InvestorRecord }>(`/admin/investors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());

  return response.investor;
}

export async function deleteInvestor(id: string): Promise<void> {
  await request<{ message: string }>(`/admin/investors/${id}`, {
    method: 'DELETE',
  }, getAdminToken());
}

export async function createInvestment(payload: {
  title: string;
  description: string;
  currency: string;
  minimumInvestment: number;
  cargoIds: string[];
  status?: InvestmentStatus;
  currentStatus?: string;
  hidden?: boolean;
  coverImageUrl?: string;
  location?: string;
}): Promise<Investment> {
  const response = await request<{ investment: Investment }>('/admin/investments', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());

  return response.investment;
}

export async function updateInvestment(id: string, payload: {
  title: string;
  description: string;
  currency: string;
  minimumInvestment: number;
  cargoIds: string[];
  status?: InvestmentStatus;
  currentStatus?: string;
  hidden?: boolean;
  coverImageUrl?: string;
  location?: string;
}): Promise<Investment> {
  const response = await request<{ investment: Investment }>(`/admin/investments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());

  return response.investment;
}

export async function deleteInvestment(id: string): Promise<void> {
  await request<{ message: string }>(`/admin/investments/${id}`, {
    method: 'DELETE',
  }, getAdminToken());
}

export async function loginInvestor(username: string, password: string): Promise<{ token: string }> {
  return request<{ token: string }>('/investor/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
}

export async function logoutInvestor(): Promise<void> {
  await request('/investor/logout', { method: 'POST' }, getInvestorToken());
  clearInvestorToken();
}

export async function getInvestorHome(): Promise<InvestorHomeResponse> {
  return request<InvestorHomeResponse>('/investor/home', { method: 'GET' }, getInvestorToken());
}

export async function updateThemePreference(themeIndex: number): Promise<void> {
  await request<{ preferredTheme: number }>('/investor/theme', {
    method: 'PATCH',
    body: JSON.stringify({ themeIndex }),
  }, getInvestorToken());
}

export async function completeInvestorKyc(payload: { avatar: string; displayName: string; preferredCurrency?: string }): Promise<InvestorProfile> {
  const response = await request<{ investor: InvestorProfile }>('/investor/kyc', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getInvestorToken());

  return response.investor;
}

export async function submitContactRequest(payload: {
  type?: 'investment' | 'contact_us';
  investmentId?: string;
  investmentTitle?: string;
  fullName: string;
  contactMethod: 'whatsapp' | 'email';
  contactDetail: string;
  rdvDate?: string;
  note?: string;
  /** Honeypot — left empty by real users; filled values are rejected server-side. */
  website?: string;
}): Promise<{ request: { _id: string } }> {
  return request<{ request: { _id: string } }>('/public/contact-request', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getAdminContactRequests(): Promise<{ requests: ContactRequest[] }> {
  return request<{ requests: ContactRequest[] }>('/admin/contact-requests', { method: 'GET' }, getAdminToken());
}

export async function updateContactRequestStatus(id: string, status: ContactRequest['status']): Promise<void> {
  await request<unknown>(`/admin/contact-requests/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }, getAdminToken());
}

export async function getAdminProductOrders(): Promise<{ orders: ProductOrder[] }> {
  return request<{ orders: ProductOrder[] }>('/admin/product-orders', { method: 'GET' }, getAdminToken());
}

export async function updateProductOrderStatus(id: string, status: ProductOrder['status']): Promise<void> {
  await request<unknown>(`/admin/product-orders/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }, getAdminToken());
}

// ---------------------------------------------------------------------------
// Avatars
// ---------------------------------------------------------------------------

export async function getPublicAvatars(): Promise<{ avatars: AvatarData[] }> {
  return request<{ avatars: AvatarData[] }>('/public/avatars', { method: 'GET' });
}

export async function getAdminAvatars(): Promise<{ avatars: AvatarData[] }> {
  return request<{ avatars: AvatarData[] }>('/admin/avatars', { method: 'GET' }, getAdminToken());
}

export async function createAvatar(payload: { name: string; imageUrl: string; defaultTheme: number; secret: boolean }): Promise<AvatarData> {
  const res = await request<{ avatar: AvatarData }>('/admin/avatars', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.avatar;
}

export async function updateAvatarMeta(id: string, payload: { name: string; defaultTheme: number; secret: boolean }): Promise<AvatarData> {
  const res = await request<{ avatar: AvatarData }>(`/admin/avatars/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.avatar;
}

export async function deleteAvatar(id: string): Promise<void> {
  await request<unknown>(`/admin/avatars/${id}`, { method: 'DELETE' }, getAdminToken());
}

// ---------------------------------------------------------------------------
// Investor — password change
// ---------------------------------------------------------------------------

export async function changeInvestorPassword(payload: { currentPassword: string; newPassword: string }): Promise<void> {
  await request<unknown>('/investor/change-password', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getInvestorToken());
}

// ---------------------------------------------------------------------------
// Shop — products & bundles
// ---------------------------------------------------------------------------

export type ShopGalleries = { earth: string[]; hands: string[] };

export type PublicBundle = {
  _id: string;
  name: string;
  imageUrl: string;
  description: string;
  price: number;
  currency: string;
  position: number;
  section: 'food' | 'artisanal';
  includedProducts: Array<{ _id: string; name: string }>;
};

export type Bundle = PublicBundle & { active: boolean; productIds: string[]; boutiqueId?: string };

export type BundleInput = {
  name: string;
  imageUrl: string;
  description: string;
  price: number;
  currency: string;
  productIds: string[];
  section: 'food' | 'artisanal';
  boutiqueId?: string;
  active: boolean;
};

export type BundleOrderInput = {
  bundleId: string;
  quantity: number;
  fullName: string;
  contactMethod: 'email' | 'whatsapp';
  contactDetail: string;
  country: string;
  message?: string;
  website?: string;
};

export async function getPublicProducts(): Promise<{ products: PublicProduct[]; galleries: ShopGalleries; bundles: PublicBundle[] }> {
  return request<{ products: PublicProduct[]; galleries: ShopGalleries; bundles: PublicBundle[] }>('/public/products', { method: 'GET' });
}

export async function getInvestorProducts(): Promise<{ products: PublicProduct[]; galleries: ShopGalleries; bundles: PublicBundle[] }> {
  return request<{ products: PublicProduct[]; galleries: ShopGalleries; bundles: PublicBundle[] }>('/investor/products', { method: 'GET' }, getInvestorToken());
}

export async function submitProductOrder(payload: ProductOrderInput): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/public/product-order', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function submitBundleOrder(payload: BundleOrderInput): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/public/bundle-order', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getAdminBundles(): Promise<{ bundles: Bundle[] }> {
  return request<{ bundles: Bundle[] }>('/admin/bundles', { method: 'GET' }, getAdminToken());
}

export async function createBundle(payload: BundleInput): Promise<Bundle> {
  const r = await request<{ bundle: Bundle }>('/admin/bundles', { method: 'POST', body: JSON.stringify(payload) }, getAdminToken());
  return r.bundle;
}

export async function updateBundle(id: string, payload: BundleInput): Promise<Bundle> {
  const r = await request<{ bundle: Bundle }>(`/admin/bundles/${id}`, { method: 'PUT', body: JSON.stringify(payload) }, getAdminToken());
  return r.bundle;
}

export async function deleteBundle(id: string): Promise<void> {
  await request<unknown>(`/admin/bundles/${id}`, { method: 'DELETE' }, getAdminToken());
}

export async function reorderBundles(order: Array<{ id: string; position: number }>): Promise<void> {
  await request<unknown>('/admin/bundles/reorder', { method: 'PUT', body: JSON.stringify({ order }) }, getAdminToken());
}

export async function getAdminProducts(): Promise<{ products: Product[] }> {
  return request<{ products: Product[] }>('/admin/products', { method: 'GET' }, getAdminToken());
}

export async function createProduct(payload: ProductInput): Promise<Product> {
  const res = await request<{ product: Product }>('/admin/products', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.product;
}

export async function updateProduct(id: string, payload: ProductInput): Promise<Product> {
  const res = await request<{ product: Product }>(`/admin/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.product;
}

export async function deleteProduct(id: string): Promise<void> {
  await request<unknown>(`/admin/products/${id}`, { method: 'DELETE' }, getAdminToken());
}

export async function reorderProducts(order: Array<{ id: string; position: number }>): Promise<void> {
  await request<unknown>('/admin/products/reorder', { method: 'PUT', body: JSON.stringify({ order }) }, getAdminToken());
}

// ---------------------------------------------------------------------------
// Partners
// ---------------------------------------------------------------------------

export async function getPublicPartners(): Promise<{ partners: Partner[] }> {
  return request<{ partners: Partner[] }>('/public/partners', { method: 'GET' });
}

export async function getAdminPartners(): Promise<{ partners: Partner[] }> {
  return request<{ partners: Partner[] }>('/admin/partners', { method: 'GET' }, getAdminToken());
}

export async function createPartner(payload: PartnerInput): Promise<Partner> {
  const res = await request<{ partner: Partner }>('/admin/partners', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.partner;
}

export async function updatePartner(id: string, payload: PartnerInput): Promise<Partner> {
  const res = await request<{ partner: Partner }>(`/admin/partners/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.partner;
}

export async function deletePartner(id: string): Promise<void> {
  await request<unknown>(`/admin/partners/${id}`, { method: 'DELETE' }, getAdminToken());
}

// ── Boutiques (public) ──────────────────────────────────────────────────────

export async function getPublicBoutiques(): Promise<{ boutiques: PublicBoutique[] }> {
  return request<{ boutiques: PublicBoutique[] }>('/public/boutiques', { method: 'GET' });
}

export async function getPublicBoutique(id: string): Promise<{ boutique: Boutique }> {
  return request<{ boutique: Boutique }>(`/public/boutiques/${id}`, { method: 'GET' });
}

export async function getPublicBoutiqueProducts(id: string): Promise<{ products: PublicProduct[] }> {
  return request<{ products: PublicProduct[] }>(`/public/boutiques/${id}/products`, { method: 'GET' });
}

export async function getPublicBoutiqueJourneys(id: string): Promise<{ journeys: Journey[] }> {
  return request<{ journeys: Journey[] }>(`/public/boutiques/${id}/journeys`, { method: 'GET' });
}

export async function getPublicBoutiqueBundles(id: string): Promise<{ bundles: PublicBundle[] }> {
  return request<{ bundles: PublicBundle[] }>(`/public/boutiques/${id}/bundles`, { method: 'GET' });
}

// ── Boutiques (admin) ──────────────────────────────────────────────────────

export async function getAdminBoutiques(): Promise<{ boutiques: Boutique[] }> {
  return request<{ boutiques: Boutique[] }>('/admin/boutiques', { method: 'GET' }, getAdminToken());
}

export async function createBoutique(payload: BoutiqueInput): Promise<Boutique> {
  const res = await request<{ boutique: Boutique }>('/admin/boutiques', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.boutique;
}

export async function updateBoutique(id: string, payload: BoutiqueInput): Promise<Boutique> {
  const res = await request<{ boutique: Boutique }>(`/admin/boutiques/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.boutique;
}

export async function deleteBoutique(id: string): Promise<void> {
  await request<unknown>(`/admin/boutiques/${id}`, { method: 'DELETE' }, getAdminToken());
}

// ── Journeys (public) ──────────────────────────────────────────────────────

export async function getPublicJourneys(): Promise<{ journeys: Journey[] }> {
  return request<{ journeys: Journey[] }>('/public/journeys', { method: 'GET' });
}

export async function getPublicJourney(id: string): Promise<{ journey: Journey }> {
  return request<{ journey: Journey }>(`/public/journeys/${id}`, { method: 'GET' });
}

export async function submitJourneyInterest(payload: JourneyInterestInput): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/public/journey-interest', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function submitWaitlist(email: string, productId: string): Promise<void> {
  await request<{ ok: boolean }>('/public/waitlist', {
    method: 'POST',
    body: JSON.stringify({ email, productId }),
  });
}

// ── Journeys (investor) ────────────────────────────────────────────────────

export async function getInvestorJourneys(): Promise<{ journeys: Journey[] }> {
  return request<{ journeys: Journey[] }>('/investor/journeys', { method: 'GET' }, getInvestorToken());
}

// ── Journeys (admin) ───────────────────────────────────────────────────────

export async function getAdminJourneys(): Promise<{ journeys: Journey[] }> {
  return request<{ journeys: Journey[] }>('/admin/journeys', { method: 'GET' }, getAdminToken());
}

export async function createJourney(payload: Omit<Journey, '_id' | 'createdAt' | 'updatedAt'>): Promise<Journey> {
  const res = await request<{ journey: Journey }>('/admin/journeys', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.journey;
}

export async function updateJourney(id: string, payload: Omit<Journey, '_id' | 'createdAt' | 'updatedAt'>): Promise<Journey> {
  const res = await request<{ journey: Journey }>(`/admin/journeys/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  }, getAdminToken());
  return res.journey;
}

export async function deleteJourney(id: string): Promise<void> {
  await request<unknown>(`/admin/journeys/${id}`, { method: 'DELETE' }, getAdminToken());
}

// ── Self-registration (auth) ───────────────────────────────────────────────

const AUTH_BASE = API_BASE.replace('/portal', '');

async function authRequest<T>(path: string, init?: RequestInit, token?: string | null): Promise<T> {
  const response = await fetch(`${AUTH_BASE}/auth${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
    ...init,
  });
  return parseJsonOrThrow<T>(response);
}

export async function registerUser(payload: { name: string; email: string; password: string }): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function verifyEmail(token: string): Promise<{ message: string; token: string }> {
  return authRequest<{ message: string; token: string }>('/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function forgotPassword(email: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token: string, password: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export async function resendVerification(email: string): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function getGoogleAuthUrl(): string {
  return `${AUTH_BASE}/auth/google`;
}

export async function unlinkGoogle(): Promise<{ message: string }> {
  return authRequest<{ message: string }>('/google/link', { method: 'DELETE' }, getInvestorToken());
}

// ── Admin registered users ─────────────────────────────────────────────────

export type RegisteredUser = {
  _id: string;
  name: string;
  username: string;
  email?: string;
  emailVerified: boolean;
  googleId: string | null;
  registrationMethod: 'email' | 'google';
  accountStatus: 'pending_verification' | 'active' | 'suspended';
  assignedInvestmentIds: string[];
  kycCompleted: boolean;
  createdAt?: string;
};

export async function getAdminRegisteredUsers(): Promise<{ users: RegisteredUser[] }> {
  return request<{ users: RegisteredUser[] }>('/admin/registered-users', { method: 'GET' }, getAdminToken());
}

export async function updateRegisteredUserStatus(id: string, status: RegisteredUser['accountStatus']): Promise<void> {
  await request<unknown>(`/admin/registered-users/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }, getAdminToken());
}

export async function deleteRegisteredUser(id: string): Promise<void> {
  await request<unknown>(`/admin/registered-users/${id}`, {
    method: 'DELETE',
  }, getAdminToken());
}

// ---------------------------------------------------------------------------
// QR Codes
// ---------------------------------------------------------------------------

export type QRCodeEntry = {
  _id: string;
  label: string;
  targetUrl: string;
  imageUrl: string;
  createdAt: string;
};

export async function getAdminQRCodes(): Promise<{ codes: QRCodeEntry[] }> {
  return request<{ codes: QRCodeEntry[] }>('/admin/qr', { method: 'GET' }, getAdminToken());
}

export async function generateQRCode(label: string, targetUrl: string): Promise<{ code: QRCodeEntry }> {
  return request<{ code: QRCodeEntry }>('/admin/qr', {
    method: 'POST',
    body: JSON.stringify({ label, targetUrl }),
  }, getAdminToken());
}

export async function deleteQRCode(id: string): Promise<void> {
  await request<unknown>(`/admin/qr/${id}`, { method: 'DELETE' }, getAdminToken());
}

// ---------------------------------------------------------------------------
// Cart — investor checkout + order history
// ---------------------------------------------------------------------------

export type CartOrderItem = {
  productId: string;
  variant?: string;
  quantity: number;
};

export type CartOrderInput = {
  items: CartOrderItem[];
  fullName: string;
  contactMethod: 'whatsapp' | 'email';
  contactDetail: string;
  country?: string;
  message?: string;
};

export async function placeCartOrder(payload: CartOrderInput): Promise<{ ok: boolean; count: number }> {
  return request<{ ok: boolean; count: number }>('/public/cart-order', {
    method: 'POST',
    body: JSON.stringify(payload),
  }, getInvestorToken() ?? undefined);
}

export type MyOrder = {
  _id: string;
  productName: string;
  variant: string;
  quantity: number;
  unitPrice: number;
  currency: string;
  total: number;
  boutiqueName?: string;
  status: 'new' | 'read' | 'contacted';
  createdAt: string;
};

export async function getMyOrders(): Promise<{ orders: MyOrder[] }> {
  return request<{ orders: MyOrder[] }>('/investor/my-orders', { method: 'GET' }, getInvestorToken());
}

export async function updateNewsletterOptin(optIn: boolean): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>('/investor/newsletter-optin', {
    method: 'PATCH',
    body: JSON.stringify({ optIn }),
  }, getInvestorToken());
}

export async function getNewsletterStats(): Promise<{ subscriberCount: number }> {
  return request<{ subscriberCount: number }>('/admin/newsletter/stats', { method: 'GET' }, getAdminToken());
}

export async function sendNewsletter(subject: string, body: string): Promise<{ sent: number; failed: number; total: number }> {
  return request<{ sent: number; failed: number; total: number }>('/admin/newsletter/send', {
    method: 'POST',
    body: JSON.stringify({ subject, body }),
  }, getAdminToken());
}

